import React, { FC, RefObject, useCallback } from 'react';
import { Group, Layout, Panel, PanelImperativeHandle, Separator } from 'react-resizable-panels';

import { SampleProteoformCountsTable, SampleProteoformsTable } from '../../Studies/components';
import studyStyles from '../../Studies/styles.module.css';

export interface CollapsibleTableRef {
  updateCollapsed: (collapsed: boolean) => void;
}

interface SampleTablesPanelGroupProps {
  orgId: string;
  studyId: string;
  numSamples: number;
  numInstrumentRuns: number;
  defaultLayout: Layout | undefined;
  onLayoutChanged: (layout: Layout) => void;
  proteoformCountRef: RefObject<PanelImperativeHandle | null>;
  proteoformsRef: RefObject<PanelImperativeHandle | null>;
  proteoformCountsTableRef: RefObject<CollapsibleTableRef | null>;
  proteoformsTableRef: RefObject<CollapsibleTableRef | null>;
  minPanelSize?: string;
  isLoading?: boolean;
}

const defaultPanelSize = 50;
export const SampleTablesPanelGroup: FC<SampleTablesPanelGroupProps> = ({
  orgId,
  studyId,
  numSamples,
  numInstrumentRuns,
  defaultLayout,
  onLayoutChanged,
  proteoformCountRef,
  proteoformsRef,
  proteoformCountsTableRef,
  proteoformsTableRef,
  minPanelSize = '47px',
}) => {
  // Memoized resize handlers for panels
  const handleProteoformCountResize = useCallback(() => {
    if (proteoformCountsTableRef.current && proteoformCountRef.current?.isCollapsed) {
      proteoformCountsTableRef.current.updateCollapsed(proteoformCountRef.current.isCollapsed());
    }
  }, [proteoformCountsTableRef, proteoformCountRef]);

  const handleProteoformsResize = useCallback(() => {
    if (proteoformsTableRef.current && proteoformsRef.current?.isCollapsed) {
      proteoformsTableRef.current.updateCollapsed(proteoformsRef.current.isCollapsed());
    }
  }, [proteoformsTableRef, proteoformsRef]);

  if (!(numInstrumentRuns > 0 && numSamples > 0)) return null;

  return (
    <Group defaultLayout={defaultLayout} onLayoutChanged={onLayoutChanged} orientation="vertical">
      <Panel
        collapsible
        collapsedSize={minPanelSize}
        id="proteoformCount"
        defaultSize={defaultPanelSize}
        minSize={minPanelSize}
        panelRef={proteoformCountRef}
        onResize={handleProteoformCountResize}
      >
        <SampleProteoformCountsTable
          adjacentPanelRef={proteoformsRef}
          orgId={orgId}
          panelRef={proteoformCountRef}
          ref={proteoformCountsTableRef}
          studyId={studyId}
        />
      </Panel>

      <Separator className={studyStyles.resizeSeparator} />

      <Panel
        collapsible
        collapsedSize={minPanelSize}
        defaultSize={defaultPanelSize}
        id="proteoforms"
        minSize={minPanelSize}
        panelRef={proteoformsRef}
        onResize={handleProteoformsResize}
      >
        <SampleProteoformsTable
          ref={proteoformsTableRef}
          adjacentPanelRef={proteoformCountRef}
          orgId={orgId}
          panelRef={proteoformsRef}
          studyId={studyId}
        />
      </Panel>
    </Group>
  );
};
